import { captureResultConfirms } from "../wizard/screen_capture_state"

export const screenCaptureLoop = {
  async start() {
    if (this.running) return
    this.hideInterruptionNotice()
    if (!this.endpoint()) {
      return this.setStatus(this.t("configuration_error", "Configuration error: missing analyze URL."), "error")
    }
    if (!this.screenShareApiAvailable()) {
      return this.setStatus(
        this.t("unavailable_with_upload", "Screen sharing is not available in this browser. Use a computer or choose Apple Wallet instead."),
        "error"
      )
    }

    if (this.wizardModeValue && !this.session) {
      const ok = await this.ensureSession()
      if (!ok) {
        return this.setStatus(this.t("session_error", "Could not start a verification session. Reload and try again."), "error")
      }
    }

    this.clearWizardResult({ preserveCaptures: true })
    this.previousFrameDataUrl = null

    try {
      this.stream = await this.requestDisplayMediaStream()
    } catch (error) {
      console.warn("screen-capture: getDisplayMedia failed", error)
      return this.setStatus(this.shareErrorMessage(error), "error")
    }

    this.videoTarget.muted = true
    this.videoTarget.srcObject = this.stream
    try {
      await this.videoTarget.play()
    } catch (error) {
      console.warn("screen-capture: video preview failed", error)
      this.teardown()
      return this.setStatus(
        this.t("preview_error", "Screen sharing started but the preview could not run. Try again or use Apple Wallet instead."),
        "error"
      )
    }

    this.running = true
    this.frameCount = 0
    this.captureStartedAt = Date.now()
    this.awaitingFinalConfirmationFrame = false
    this.finalConfirmationFramesCaptured = 0
    if (this.hasStartButtonTarget) this.startButtonTarget.disabled = true
    this.markSubstepShareActive(true)
    this.renderCapturedState()
    this.setStatus(this.t("sharing", "Sharing… switch to the verification tab and open the program."), "info")
    this.stream.getVideoTracks()[0]?.addEventListener("ended", () => this.handleShareEnded())

    await this.tick()
    this.scheduleNextTick()
  },

  scheduleNextTick() {
    if (!this.running || this.timer) return

    this.timer = window.setTimeout(async () => {
      this.timer = null
      await this.tick()
      this.scheduleNextTick()
    }, this.intervalMsValue)
  },

  async tick() {
    if (!this.running) return
    this.frameCount += 1
    const confirmationAllowance = this.awaitingFinalConfirmationFrame
      ? this.finalConfirmationFramesValue
      : 0
    if (this.frameCount > this.maxFramesValue + confirmationAllowance) {
      this.setStatus(this.t("maximum_attempts", "Maximum capture attempts reached."), "error")
      this.teardown()
      return
    }

    const video = this.videoTarget
    const { videoWidth: width, videoHeight: height } = video
    if (!width || !height) return this.setStatus(this.t("waiting_video", "Waiting for video…"), "info")

    const maxEdge = 1600
    const scale = width > maxEdge || height > maxEdge ? maxEdge / Math.max(width, height) : 1
    const targetWidth = Math.round(width * scale)
    const targetHeight = Math.round(height * scale)
    const canvas = this.canvasTarget
    canvas.width = targetWidth
    canvas.height = targetHeight
    canvas.getContext("2d").drawImage(video, 0, 0, targetWidth, targetHeight)
    const dataUrl = canvas.toDataURL("image/jpeg", 0.88)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    const payload = this.wizardModeValue
      ? {
          image_base64: dataUrl,
          screen_share_session_id: this.session?.id || null,
          status_match_application: this.collectApplicationFields()
        }
      : { image_base64: dataUrl }
    if (this.previousFrameDataUrl) payload.previous_image_base64 = this.previousFrameDataUrl

    try {
      const response = await fetch(this.endpoint(), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
        body: JSON.stringify(payload)
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        const message = this.t("analysis_failed", "Analysis failed (%{status})")
          .replace("%{status}", String(response.status))
        return this.setStatus(data.error || message, "error")
      }

      if (!data.frame_inconsistent) this.previousFrameDataUrl = dataUrl
      this.persistWizardResult(data)
      if (this.wizardModeValue) await this.attachUsefulFrameToForm(dataUrl)

      if (this.detailsCaptureComplete() && !this.completionReady()) {
        if (!this.awaitingFinalConfirmationFrame) {
          this.awaitingFinalConfirmationFrame = true
          this.setStatus(
            this.t(
              "final_confirmation",
              "Details captured. Keep the airline tab visible while we take one final confirmation screenshot…"
            ),
            "info"
          )
          return
        }

        const confirmed = captureResultConfirms(
          data,
          this.collectApplicationFields(),
          this.minimumConfidenceValue
        )
        if (!confirmed) {
          this.setStatus(
            this.t(
              "final_confirmation_retry",
              "Keep the airline tab visible for one more clear confirmation screenshot…"
            ),
            "info"
          )
          return
        }

        this.finalConfirmationFramesCaptured += 1
        if (!this.completionReady()) return
        this.persistWizardResult(data)
      }

      if (this.completionReady()) {
        this.setStatus(
          this.wizardModeValue
            ? this.t("complete_returning", "All required details were captured. Returning you to the form…")
            : this.t("complete_continue", "All required details were captured. You can continue."),
          "success"
        )
        this.teardown()
        this.markSubstepShareActive(false)
        if (this.wizardModeValue) this.celebrateCompletion()
        this.bringUserBackToApplication()
        return
      }

      if (this.summaryComplete()) {
        const seconds = String(this.minimumCaptureSecondsRemaining())
        this.setStatus(
          this.t(
            "minimum_duration",
            "Details matched. Keep the airline tab visible for %{seconds} more seconds while we capture supporting frames…"
          ).replace("%{seconds}", seconds),
          "info"
        )
        return
      }

      const progressReason = data.frame_inconsistent
        ? this.t("frame_inconsistent", "The screen changed in a way that looks injected. Stay on the official program page without overlays.")
        : data.reason
      this.setStatus(
        `${this.t("still_looking", "Still looking…")} (${this.frameCount}/${this.maxFramesValue}) ${this.captureProgressReason(progressReason)}`.trim(),
        data.frame_inconsistent ? "error" : "info"
      )
    } catch {
      this.setStatus(this.t("network_error", "Network error while analyzing frame."), "error")
    }
  },

  minimumCaptureDurationReached() {
    return this.captureStartedAt != null &&
      Date.now() - this.captureStartedAt >= this.minimumDurationMsValue
  },

  minimumCaptureSecondsRemaining() {
    if (this.captureStartedAt == null) return Math.ceil(this.minimumDurationMsValue / 1000)

    const remaining = this.minimumDurationMsValue - (Date.now() - this.captureStartedAt)
    return Math.max(0, Math.ceil(remaining / 1000))
  }
}
